import {useCallback, useEffect, useState} from 'react';
import type {ControlState, EligibilityReason} from './controlState';
import {deriveControlState} from './controlState';
import {
	getAccountLimitNotice,
	getAvailableAgents,
	getRunState,
	invoke,
	isAtRunCap,
	markRead,
	stop,
	subscribeToAnyRunStateChange,
	subscribeToRunState,
} from './novaInvocationStore';

export interface NovaInvocationStateResult {
	state: ControlState;
	accountLimitNotice: 'near_limit' | 'at_limit' | null;
	onAction: () => void;
	onOpenOutput: () => void;
	onStop: () => void;
}

const IDLE_RUN_STATE = {hasEverRun: false, isRunning: false, outputRead: false, outcome: null};

export function useNovaInvocationState(
	taskId: string,
	eligibility: EligibilityReason | null,
	// The caller (ConnectedNovaInvocationControl) must still call this hook unconditionally on
	// every render regardless of the feature flag — Rules of Hooks forbids skipping a hook call
	// itself. Passing `enabled` in lets the flag-off case skip the store subscriptions and their
	// per-render-and-per-event cost internally instead, since its result is discarded either way.
	enabled = true,
	// Threaded straight through to invoke() for the notification it may raise on completion — this
	// hook has no other way to know a task's display name, and re-deriving it from taskId alone
	// isn't possible.
	taskName?: string,
	// Same reasoning as taskName — lets the notification link to the task from anywhere, not just
	// the page the run started on.
	companyProjectId?: string | number,
	companyTaskId?: string | number,
	// For the notification's project-code pill — same reasoning as companyProjectId/companyTaskId.
	projectColor?: string
): NovaInvocationStateResult {
	const [runState, setRunState] = useState(() => (enabled ? getRunState(taskId) : IDLE_RUN_STATE));
	const [atRunCap, setAtRunCap] = useState(() => enabled && isAtRunCap());
	const [accountLimitNotice, setAccountLimitNotice] = useState(() => (enabled ? getAccountLimitNotice() : null));

	useEffect(() => {
		if (!enabled) return undefined;
		return subscribeToRunState(taskId, () => setRunState(getRunState(taskId)));
	}, [taskId, enabled]);
	// Both are properties of the whole tracked set, not of this one task — another task starting
	// or finishing must be able to flip this idle control's disabled state and its account-limit
	// banner too. atRunCap only flips exactly at the cap boundary, so accountLimitNotice needs its
	// own read here too — otherwise an idle control never re-renders (and so never picks up the
	// notice) for the near_limit case, which sits below that boundary.
	useEffect(() => {
		if (!enabled) return undefined;
		return subscribeToAnyRunStateChange(() => {
			setAtRunCap(isAtRunCap());
			setAccountLimitNotice(getAccountLimitNotice());
		});
	}, [enabled]);

	const state = deriveControlState({...runState, eligibility, atRunCap});

	// There is no account-default agent yet (that decision is still open), so a bare Action click
	// — one that never went through the popover — falls back to whichever agent happens to be
	// first in the resolved list, rather than leaving the run agent-less. A user picking an agent
	// from the popover calls novaInvocationStore.invoke(taskId, agentId) directly (see
	// NovaAgentPickerForTask) — a separate call site, not through this hook — and that explicit
	// choice is never overridden here.
	const onAction = useCallback(
		() => invoke(taskId, getAvailableAgents()[0]?.id, {taskName, companyProjectId, companyTaskId, projectColor}),
		[taskId, taskName, companyProjectId, companyTaskId, projectColor]
	);
	const onOpenOutput = useCallback(() => markRead(taskId), [taskId]);
	const onStop = useCallback(() => stop(taskId), [taskId]);

	return {state, accountLimitNotice, onAction, onOpenOutput, onStop};
}
